package com.trevorwiebe.timesheet.admin.equipment.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.domain.formatWithCommas
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.EquipmentType
import com.trevorwiebe.timesheet.mileage.domain.model.isServiceDue
import org.koin.compose.viewmodel.koinViewModel

private val naturalSortComparator = Comparator<String> { a, b ->
    val tokenRegex = Regex("(\\d+)|(\\D+)")
    val aTokens = tokenRegex.findAll(a).map { it.value }.toList()
    val bTokens = tokenRegex.findAll(b).map { it.value }.toList()
    val len = minOf(aTokens.size, bTokens.size)
    for (i in 0 until len) {
        val aNum = aTokens[i].toLongOrNull()
        val bNum = bTokens[i].toLongOrNull()
        val cmp = when {
            aNum != null && bNum != null -> aNum.compareTo(bNum)
            else -> aTokens[i].compareTo(bTokens[i], ignoreCase = true)
        }
        if (cmp != 0) return@Comparator cmp
    }
    aTokens.size.compareTo(bTokens.size)
}

@Composable
fun EquipmentScreen(
    viewModel: EquipmentViewModel = koinViewModel(),
    onBack: () -> Unit
) {
    val state by viewModel.state.collectAsState()

    val filteredEquipment = run {
        val activeFilters = state.activeFilters
        val typeFilters = activeFilters.intersect(setOf(EquipmentFilter.TRUCK, EquipmentFilter.TRAILER))
        val statusFilters = activeFilters.intersect(setOf(EquipmentFilter.ACTIVE, EquipmentFilter.INACTIVE, EquipmentFilter.DELETED))

        val byType = if (typeFilters.isEmpty()) {
            state.equipment
        } else {
            state.equipment.filter { equipment ->
                (EquipmentFilter.TRUCK in typeFilters && equipment.type == EquipmentType.TRUCK) ||
                (EquipmentFilter.TRAILER in typeFilters && equipment.type == EquipmentType.TRAILER)
            }
        }

        val byStatus = if (statusFilters.isEmpty()) {
            byType.filter { !it.deleted }
        } else {
            byType.filter { equipment ->
                (EquipmentFilter.ACTIVE in statusFilters && equipment.active && !equipment.deleted) ||
                (EquipmentFilter.INACTIVE in statusFilters && !equipment.active && !equipment.deleted) ||
                (EquipmentFilter.DELETED in statusFilters && equipment.deleted)
            }
        }

        when (state.activeSort) {
            EquipmentSort.UNIT_NUMBER -> byStatus.sortedWith(compareBy(naturalSortComparator) { it.unitNumber })
            EquipmentSort.MILES_SINCE_SERVICE -> byStatus.sortedWith(
                compareByDescending<Equipment> { state.serviceStatusMap[it.id] ?: -1L }
                    .thenBy { if (it.type == EquipmentType.TRAILER) 1 else 0 }
            )
            null -> byStatus
        }
    }

    Scaffold(
        topBar = {
            TopBar(
                title = "Equipment",
                backIcon = { BackIcon() },
                onBack = onBack,
                actions = {
                    EquipmentTopBarAddAction(
                        onClick = { viewModel.onEvent(EquipmentEvent.OnAddEquipmentClick) }
                    )
                }
            )
        },
        floatingActionButton = {
            EquipmentFab(onClick = { viewModel.onEvent(EquipmentEvent.OnAddEquipmentClick) })
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        Column(
            modifier = Modifier
                .fillMaxSize()
        ) {
            EquipmentFilterChipRow(
                activeFilters = state.activeFilters,
                onFilterToggled = { viewModel.onEvent(EquipmentEvent.OnFilterToggled(it)) }
            )
            HorizontalDivider()
            EquipmentSortChipRow(
                activeSort = state.activeSort,
                onSortChanged = { viewModel.onEvent(EquipmentEvent.OnSortChanged(it)) }
            )
            HorizontalDivider()
            if (state.isLoadingServiceStatus) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(
                        color = MaterialTheme.colorScheme.tertiary
                    )
                }
            } else {
                LazyColumn(modifier = Modifier.fillMaxSize()) {
                    items(filteredEquipment) { equipment ->
                        EquipmentRow(
                            equipment = equipment,
                            mileagesSinceService = state.serviceStatusMap[equipment.id],
                            onClick = { viewModel.onEvent(EquipmentEvent.OnEditEquipmentClick(equipment)) }
                        )
                    }
                }
            }
        }

        NativeDialog(
            visible = state.showMakeActiveDialog,
            title = "Make Active?",
            message = "Do you also want to mark this equipment as active?",
            confirmText = "Yes",
            onConfirm = { viewModel.onEvent(EquipmentEvent.OnMakeActiveAfterRestore(makeActive = true)) },
            dismissText = "No",
            onDismiss = { viewModel.onEvent(EquipmentEvent.OnMakeActiveAfterRestore(makeActive = false)) }
        )

        if (state.showAddEditDialog) {
            AddEditEquipmentDialog(
                editing = state.editingEquipment,
                onDismiss = { viewModel.onEvent(EquipmentEvent.OnDismissDialog) },
                onSave = { unitNumber, type, active, serviceInterval, serviceWarningMiles ->
                    viewModel.onEvent(
                        EquipmentEvent.OnSaveEquipment(unitNumber, type, active, serviceInterval, serviceWarningMiles)
                    )
                },
                onDelete = { viewModel.onEvent(EquipmentEvent.OnDeleteEquipment) },
                onRestore = { viewModel.onEvent(EquipmentEvent.OnRestoreEquipment) }
            )
        }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun EquipmentFilterChipRow(
    activeFilters: Set<EquipmentFilter>,
    onFilterToggled: (EquipmentFilter) -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy((-8).dp)
    ) {
        Text(
            text = "Filter",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
        )
        EquipmentFilter.entries.forEach { filter ->
            val selected = filter in activeFilters
            FilterChip(
                selected = selected,
                onClick = { onFilterToggled(filter) },
                label = { Text(filter.name.lowercase().replaceFirstChar { it.uppercase() }) },
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    borderColor = MaterialTheme.colorScheme.tertiary,
                    selected = selected
                ),
                colors = FilterChipDefaults.filterChipColors(
                    containerColor = MaterialTheme.colorScheme.background,
                    selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                    selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                )
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun EquipmentSortChipRow(
    activeSort: EquipmentSort?,
    onSortChanged: (EquipmentSort?) -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy((-8).dp)
    ) {
        Text(
            text = "Sort by",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
        )
        listOf(
            EquipmentSort.UNIT_NUMBER to "Unit Number",
            EquipmentSort.MILES_SINCE_SERVICE to "Miles Since Service"
        ).forEach { (sort, label) ->
            val selected = activeSort == sort
            FilterChip(
                selected = selected,
                onClick = { onSortChanged(if (selected) null else sort) },
                label = { Text(label) },
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    borderColor = MaterialTheme.colorScheme.tertiary,
                    selected = selected
                ),
                colors = FilterChipDefaults.filterChipColors(
                    containerColor = MaterialTheme.colorScheme.background,
                    selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                    selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                )
            )
        }
    }
}

@Composable
private fun EquipmentRow(
    equipment: Equipment,
    mileagesSinceService: Long?,
    onClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    start = 16.dp, end = 16.dp,
                    top = 12.dp,
                    bottom = if (mileagesSinceService != null && equipment.serviceInterval != 0L) 0.dp else 12.dp
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                modifier = Modifier.weight(1f),
                text = equipment.unitNumber.ifEmpty { "(no unit number)" },
                fontSize = 16.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )

            Spacer(
                modifier = Modifier.weight(1f)
            )

            Text(
                text = "${equipment.type.name.lowercase().replaceFirstChar { it.uppercase() }} " +
                        "· ${if(equipment.serviceInterval != 0L) "${equipment.serviceInterval.formatWithCommas()} svc interval · " else ""}",
                color = if (equipment.active) MaterialTheme.colorScheme.secondary
                else MaterialTheme.colorScheme.secondary.copy(alpha = 0.5f),
                fontSize = 14.sp,
                maxLines = 1
            )
            Text(
                text = if (equipment.active) "Active" else "Inactive",
                color = if (equipment.active) MaterialTheme.colorScheme.tertiary
                else MaterialTheme.colorScheme.secondary.copy(alpha = 0.5f),
                fontSize = 14.sp,
                maxLines = 1,
                fontWeight = FontWeight.SemiBold
            )
        }
        if (mileagesSinceService != null && equipment.serviceInterval != 0L) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 16.dp, end = 16.dp, bottom = 12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "${mileagesSinceService.formatWithCommas()} mi since service",
                    fontSize = 14.sp,
                    color = MaterialTheme.colorScheme.secondary
                )
                if (equipment.isServiceDue(mileagesSinceService)) {
                    Text(
                        text = "Service Needed",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.error
                    )
                }
            }
        }
        HorizontalDivider()
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AddEditEquipmentDialog(
    editing: Equipment?,
    onDismiss: () -> Unit,
    onSave: (unitNumber: String, type: EquipmentType, active: Boolean, serviceInterval: Long, serviceWarningMiles: Long) -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit
) {
    var unitNumber by remember { mutableStateOf(editing?.unitNumber ?: "") }
    var selectedType by remember { mutableStateOf(editing?.type ?: EquipmentType.TRUCK) }
    var active by remember { mutableStateOf(editing?.active ?: true) }
    var serviceIntervalText by remember { mutableStateOf(editing?.serviceInterval?.toString() ?: "") }
    var serviceWarningMilesText by remember { mutableStateOf(editing?.serviceWarningMiles?.toString() ?: "2000") }
    var typeDropdownExpanded by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }

    NativeDialog(
        visible = showDeleteConfirm,
        title = "Delete Equipment",
        message = "Are you sure you want to delete this equipment? This can be restored.",
        confirmText = "Delete",
        onConfirm = onDelete,
        dismissText = "Cancel",
        onDismiss = { showDeleteConfirm = false }
    )

    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier.responsiveDialogWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = if (editing == null) "Add Equipment" else "Edit Equipment",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(16.dp))

                TimesheetTextField(
                    text = unitNumber,
                    label = "Unit Number",
                    onTextChange = { unitNumber = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                ExposedDropdownMenuBox(
                    expanded = typeDropdownExpanded,
                    onExpandedChange = { typeDropdownExpanded = it }
                ) {
                    TimesheetTextField(
                        modifier = Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
                        text = selectedType.name.lowercase().replaceFirstChar { it.uppercase() },
                        label = "Type",
                        readOnly = true,
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = typeDropdownExpanded) }
                    )
                    ExposedDropdownMenu(
                        expanded = typeDropdownExpanded,
                        onDismissRequest = { typeDropdownExpanded = false }
                    ) {
                        EquipmentType.entries.forEach { type ->
                            DropdownMenuItem(
                                text = { Text(type.name.lowercase().replaceFirstChar { it.uppercase() }) },
                                onClick = {
                                    selectedType = type
                                    typeDropdownExpanded = false
                                }
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = serviceIntervalText,
                    label = "Service Interval (mi)",
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    onTextChange = { serviceIntervalText = it.filter { c -> c.isDigit() } }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = serviceWarningMilesText,
                    label = "Service Warning (mi before due)",
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    onTextChange = { serviceWarningMilesText = it.filter { c -> c.isDigit() } }
                )
                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Active", fontSize = 16.sp)
                    Switch(
                        checked = active,
                        onCheckedChange = { active = it },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = MaterialTheme.colorScheme.tertiary,
                            checkedTrackColor = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.5f)
                        )
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))

                val isDeleted = editing?.deleted == true
                DialogButtonRow(
                    confirmButtonText = "Save",
                    confirmEnabled = unitNumber.isNotBlank() && !isDeleted,
                    onConfirmClick = {
                        onSave(
                            unitNumber.trim(),
                            selectedType,
                            active,
                            serviceIntervalText.toLongOrNull() ?: 0L,
                            serviceWarningMilesText.toLongOrNull() ?: 2000L
                        )
                    },
                    dismissButtonText = "Cancel",
                    onDismissClick = onDismiss,
                    neutralButtonText = when {
                        editing == null -> null
                        isDeleted -> "Restore"
                        else -> "Delete"
                    },
                    onNeutralClick = if (isDeleted) onRestore else { { showDeleteConfirm = true } }
                )
            }
        }
    }
}
